import { RefObject, useEffect } from "react";

interface Particle {
  x0: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  s: number;
  travel: number;
  lav: boolean;
  flash: number;
  flashDur: number;
  ph: number;
}

// Particle motion, density, colors and twinkle timing from the supplied V5 handoff.
export function useBannerParticles(
  canvas: RefObject<HTMLCanvasElement>,
  artwork: RefObject<HTMLImageElement>
) {
  useEffect(() => {
    const cv = canvas.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf = 0;
    const motionPreference = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    );
    let reduce = motionPreference?.matches ?? false;
    let W = 0;
    let H = 0;
    let cx = 0;
    let cy = 0;
    let R = 0;
    let P: Particle[] = [];
    const spawn = (init: boolean): Particle => {
      const a = Math.PI * (0.55 + Math.random() * 0.9);
      const rr = R * (0.92 + Math.random() * 0.16);
      const x0 = cx + Math.cos(a) * rr * 0.8;
      const y0 = cy + Math.sin(a) * rr;
      const travel = W * (0.25 + Math.random() * 0.35);
      const p = {
        x0,
        x: x0,
        y: y0,
        vx: -(0.1 + Math.random() * 0.22),
        vy: 0,
        s: 0.4 + Math.random() * 0.9,
        travel,
        lav: Math.random() < 0.65,
        flash: 0,
        flashDur: 0,
        ph: Math.random() * 6.28,
      };
      if (init) p.x = x0 - Math.random() * travel;
      return p;
    };
    const size = () => {
      const b = cv.getBoundingClientRect();
      W = b.width;
      H = b.height;
      cv.width = W * dpr;
      cv.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const img = artwork.current;
      const ib = img && img.getBoundingClientRect();
      if (ib && ib.width) {
        cx = ib.left - b.left + ib.width * 0.5;
        cy = Math.min(H * 0.55, ib.top - b.top + ib.height * 0.5);
        R = Math.min(ib.height, H) * 0.36;
      } else {
        cx = W * 0.8;
        cy = H * 0.5;
        R = H * 0.36;
      }
      P = Array.from({ length: Math.round(Math.min(70, W / 11)) }, () =>
        spawn(true)
      );
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    };
    let last = performance.now();
    const tick = (t: number) => {
      if (
        !reduce &&
        (document.hidden || document.querySelector('[role="dialog"]'))
      ) {
        last = t;
        raf = requestAnimationFrame(tick);
        return;
      }
      const dt = reduce ? 0 : Math.min(48, t - last) / 16.67;
      last = t;
      ctx.clearRect(0, 0, W, H);
      for (let i = 0; i < P.length; i += 1) {
        const p = P[i];
        p.x += p.vx * dt;
        const k = (p.x0 - p.x) / p.travel;
        if (k >= 1 || p.x < -4) {
          P[i] = spawn(false);
        } else {
          const fadeIn = Math.min(1, k * 12);
          const base = fadeIn * (1 - k) ** 1.6 * 0.7;
          if (p.flash <= 0 && Math.random() < 0.0008 * dt) {
            p.flashDur = 14 + Math.random() * 12;
            p.flash = p.flashDur;
          }
          let al = base;
          let r = p.s;
          let f = 0;
          if (p.flash > 0) {
            f = Math.sin((1 - p.flash / p.flashDur) * Math.PI);
            p.flash -= dt;
            al = Math.min(0.85, base + f * 0.45 * (1 - k * 0.5));
            r = p.s * (1 + f * 0.5);
          }
          const col = p.lav ? "185,168,255" : "255,255,255";
          ctx.shadowBlur = r * (6 + f * 4);
          ctx.shadowColor = p.lav
            ? "rgba(136,86,255,0.9)"
            : "rgba(255,255,255,0.8)";
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${col},${al})`;
          ctx.fill();
          if (f > 0.3) {
            const L = r * (2 + f * 2.5);
            ctx.shadowBlur = 0;
            ctx.strokeStyle = `rgba(255,255,255,${(f - 0.3) * 0.35})`;
            ctx.lineWidth = 0.4;
            ctx.beginPath();
            ctx.moveTo(p.x - L, p.y);
            ctx.lineTo(p.x + L, p.y);
            ctx.moveTo(p.x, p.y - L);
            ctx.lineTo(p.x, p.y + L);
            ctx.stroke();
          }
        }
      }
      if (!reduce) raf = requestAnimationFrame(tick);
    };
    const onMotionChange = () => {
      reduce = motionPreference?.matches ?? false;
      last = performance.now();
      size();
    };
    motionPreference?.addEventListener?.("change", onMotionChange);
    size();
    const observer = new ResizeObserver(size);
    observer.observe(cv);
    const img = artwork.current;
    if (img && !img.complete)
      img.addEventListener("load", size, { once: true });

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      motionPreference?.removeEventListener?.("change", onMotionChange);
      img?.removeEventListener("load", size);
    };
  }, [canvas, artwork]);
}
